import type { TileSet } from './tiles';
import { pairs, type WorldMapSpec } from './worldSpec';

/** Dustwood Frontier's painting, tiled as public/world/frontier/v2 at WebP Q77
 *  (the highest at which the opening view stays inside its 1.5 MB budget at both
 *  phone sizes: 1,238 KB at 390x844 and 1,500 KB at 375x667 with every coarser
 *  level fetched; the cutting script's own worst case is 1,388 KB). This is the
 *  31 October painting with the winding dirt path (Jon: "just a dirt path
 *  through an old western town instead" of the railway); v1 is the straight-track
 *  painting, kept because tiles are served as immutable files that an open tab
 *  may still ask for. A changed painting goes in a new version folder, with its
 *  spots measured again. */
export const FRONTIER_TILES: TileSet = {
  base: '/world/frontier/v2/map_files',
  width: 3072,
  height: 2048,
  tileSize: 512,
  top: 3,
};

/**
 * Dustwood Frontier, from the painting's spots.json: a red-rock desert valley
 * and its seven buildings (node ids 22 to 28, the world's slots in order) along
 * a winding packed-dirt path with wheel ruts, bordered by cobbles, that runs
 * from the west edge to the east, behind the Gold Mine's mound, the Bounty's
 * cap and the barn's roof edge. The road is that path's centre line (about 84
 * px wide; the spec's width of 100 keeps the cobbles' edge clear). The Gold
 * Mine's, the Bounty's, the Iron Horse's and High Noon's outlines hug thin
 * structures (the mound and its rails, the notice board, the water tower, the
 * clock tower), so the Bounty, the Iron Horse and High Noon carry a padded `tap`
 * shape a finger can hit; the Gold Mine is tapped by its outline. There is no
 * water, no painted still area and no train (the railway was painted out on
 * 31 October); the mine's own two rails inside the mound are the only track.
 *
 * Every plate hangs where the painting leaves room beside or above its
 * building (Jon, 26 October), clear of every outline, the pieces' places and
 * the path: above the Reels, the Saloon, the Iron Horse and High Noon (which
 * stand close to the road), and below the Gold Mine, the Bounty and the
 * Stampede, whose sides and tops are the road, the neighbours' ground or the
 * pieces' (a plate beside them would sit more than half a screen from them,
 * and the map hides a plate whose spot a phone's view does not reach). A narrow
 * two-line plate takes the smallest phone where a wide one would read as a
 * neighbour's.
 */
export const FRONTIER: WorldMapSpec = {
  biome: 'frontier',
  tiles: FRONTIER_TILES,
  buildings: [
    {
      node: 22,
      name: 'Dustwood Reels',
      centre: [350, 660],
      plate: [330, 248],
      plateTight: [330, 220],
      plateNarrow: true,
      outline: pairs([
        330, 368, 372, 380, 374, 420, 430, 450, 500, 500, 562, 540, 566, 600, 616, 650, 624, 690,
        620, 780, 580, 830, 570, 876, 510, 894, 430, 904, 350, 892, 240, 904, 180, 880, 100, 850,
        80, 800, 70, 690, 124, 660, 124, 544, 220, 480, 300, 430, 294, 392,
      ]),
    },
    {
      node: 23,
      name: 'Gold Mine',
      centre: [730, 1080],
      plate: [690, 1321],
      plateTight: [680, 1321],
      outline: pairs([
        640, 840, 760, 840, 850, 880, 884, 940, 960, 1030, 1000, 1120, 994, 1200, 900, 1256, 784,
        1280, 704, 1310, 590, 1290, 536, 1184, 462, 1100, 524, 1000, 590, 940,
      ]),
    },
    {
      node: 24,
      name: 'Saloon',
      centre: [1220, 700],
      plate: [1214, 348],
      plateTight: [1214, 320],
      plateNarrow: true,
      outline: pairs([
        1214, 468, 1250, 500, 1370, 550, 1400, 560, 1400, 660, 1420, 710, 1434, 730, 1470, 800,
        1460, 840, 1420, 870, 1370, 880, 1320, 890, 1230, 900, 1150, 920, 1080, 900, 1030, 840,
        1000, 820, 980, 780, 960, 700, 1000, 664, 1000, 584, 1030, 570, 1110, 530, 1200, 470,
      ]),
    },
    {
      node: 25,
      name: 'Bounty',
      centre: [1490, 1140],
      plate: [1350, 1371],
      plateTight: [1286, 1371],
      outline: pairs([
        1500, 912, 1580, 940, 1636, 976, 1716, 1010, 1716, 1160, 1700, 1224, 1650, 1250, 1600,
        1296, 1490, 1300, 1420, 1280, 1410, 1360, 1280, 1360, 1256, 1320, 1254, 1180, 1280, 1150,
        1284, 1050, 1360, 974, 1440, 940,
      ]),
      tap: pairs([
        1734, 1000, 1584, 920, 1502, 892, 1348, 958, 1266, 1040, 1234, 1170, 1236, 1326, 1260,
        1368, 1280, 1380, 1420, 1378, 1604, 1316, 1710, 1242, 1736, 1168,
      ]),
    },
    {
      node: 26,
      name: 'Iron Horse',
      centre: [1920, 780],
      plate: [2100, 404],
      plateTight: [2100, 376],
      plateNarrow: true,
      outline: pairs([
        1752, 616, 1800, 610, 1920, 600, 2000, 600, 2016, 560, 2044, 532, 2100, 524, 2156, 550,
        2160, 600, 2156, 660, 2180, 720, 2200, 800, 2204, 860, 2216, 924, 2216, 1016, 2120, 1016,
        2000, 1006, 1900, 1004, 1800, 984, 1720, 940, 1700, 900, 1690, 830, 1670, 756, 1740, 670,
      ]),
      tap: pairs([
        2174, 540, 2094, 504, 2026, 516, 1740, 600, 1650, 750, 1680, 908, 1700, 948, 1792, 1002,
        1896, 1024, 2102, 1036, 2228, 1032, 2236, 1022, 2236, 912,
      ]),
    },
    {
      node: 27,
      name: 'Stampede',
      centre: [2340, 1240],
      plate: [2063, 1556],
      plateTight: [2076, 1556],
      plateNarrow: true,
      outline: pairs([
        2330, 1010, 2400, 1024, 2490, 1090, 2530, 1110, 2600, 1160, 2640, 1190, 2644, 1250, 2690,
        1300, 2684, 1400, 2600, 1444, 2400, 1470, 2200, 1460, 2120, 1436, 2040, 1400, 2010, 1340,
        2020, 1280, 2040, 1240, 2080, 1180, 2076, 1150, 2200, 1090, 2260, 1040,
      ]),
    },
    {
      node: 28,
      name: 'High Noon',
      centre: [2770, 720],
      plate: [2750, 244],
      plateTight: [2750, 216],
      plateNarrow: true,
      outline: pairs([
        2750, 364, 2780, 410, 2810, 430, 2816, 600, 2880, 630, 2930, 660, 2940, 720, 3000, 770,
        3004, 860, 2990, 940, 2976, 1000, 2910, 1010, 2860, 994, 2800, 1006, 2704, 994, 2640, 940,
        2600, 924, 2550, 910, 2540, 840, 2536, 780, 2570, 730, 2580, 660, 2660, 630, 2684, 610,
        2690, 420, 2720, 410,
      ]),
      tap: pairs([
        2742, 346, 2672, 410, 2548, 684, 2516, 772, 2530, 916, 2704, 1014, 2884, 1030, 2984, 1018,
        2998, 1000, 3024, 870, 3020, 764, 2830, 422, 2762, 348,
      ]),
    },
  ],
  // The dirt path's centre line (work/path_v3/path_centre.json of the approved
  // painting, about every 24 px), reduced to the 40 points that keep it within
  // 3 px, from the west edge to the east. It meets each building in slot
  // order; the Saloon and the Bounty are both nearest to the stretch at
  // x 1440 to 1490 (21 px apart along it), since the path runs 300 px south of
  // the Saloon and 200 px north of the Bounty.
  road: pairs([
    0, 869, 105, 931, 171, 962, 238, 987, 285, 994, 357, 994, 405, 987, 474, 965, 581, 912,
    627, 901, 676, 900, 866, 918, 910, 936, 992, 987, 1057, 1019, 1126, 1035, 1198, 1035,
    1246, 1029, 1292, 1016, 1337, 997, 1441, 939, 1486, 924, 1511, 923, 1605, 935, 1651, 948,
    1865, 1058, 1934, 1078, 2006, 1080, 2077, 1071, 2146, 1050, 2301, 986, 2372, 975, 2420, 981,
    2466, 994, 2709, 1098, 2777, 1121, 2846, 1139, 2895, 1143, 2966, 1140, 3072, 1105
  ]),
  roadWidth: 100,
  still: [],
  // The painting has no lamps along its road (the five it paints hang on its
  // buildings), so the posts the code draws stand where each was proved on open
  // ground beside the path (a 2x crop at every foot, 31 October): on dirt or
  // cobbles clear of the path's ruts and its cobbled border, and off fences,
  // rocks, cacti, shrubs and props, the buildings, the plates and the pieces'
  // places. The painting is crowded beside the path at the Iron Horse's porch and
  // the barn, so the gaps there run to about 380 px (the rest are 154 to 340).
  lanternPosts: pairs([
    290, 925, 585, 830, 879, 834, 1040, 913, 1273, 947, 1626, 801, 1952, 1200, 2305, 918, 2508,
    934, 2660, 1000, 2928, 1066,
  ]),
  plateBox: [420, 110],
  plateBoxTight: [528, 138],
  plateBoxNarrow: [394, 138],
  ambient: '/world/frontier/ambient/v2',
  ambientFiles: ['life.json', 'life.webp', 'sway.webp'],
  // Dustwood Frontier's road friends' kinds: no armadillos, tortoises, donkeys,
  // burros or mules, rabbits, hares or jackalopes in its life (cowboys, cattle,
  // horses, a hawk and the mine cart stand in).
  forbidden: ['armadillo', 'tortoise', 'donkey', 'burro', 'mule', 'rabbit', 'hare', 'jackalope'],
};
